import React from 'react'
import { NonIdealState } from '@blueprintjs/core'
import { match } from 'ts-pattern'
import { PrettyJson, InlineSpinnerIcon, ErrorState  } from 'ui-components'
import {
  AssetPropertiesT,
  GetAssetUrlQuery,
} from 'ui-io/assets-io'

import { useQuery } from 'react-query'

type TextContentType =
  | 'text/csv'
  | 'text/plain'
  | 'text/rtf'
  | 'text/tab-separated-values'

export const AssetTextContent = ({
  contentType,
  assetProperties: { filesize },
  getAssetUrlQuery,
}: {
  contentType: 'application/json' | TextContentType
  assetProperties: AssetPropertiesT
  getAssetUrlQuery: GetAssetUrlQuery
}) => {
  const assetUrl = getAssetUrlQuery({
    attachment: false,
    compressed: false,
  })
  const isReasonablySized = filesize <= 1e5 // smaller than 100kB

  const connection = useQuery({
    queryKey: ['assetContents', assetUrl],
    queryFn: async () => {
      const response = await fetch(assetUrl)
      return contentType !== 'application/json'
        ? {
            dataType: 'text' as const,
            contents: await response.text(),
          }
        : { dataType: 'json' as const, contents: await response.json() }
    },
    enabled: isReasonablySized,
  })

  if (!isReasonablySized) {
    return (
      <NonIdealState
        title="Size of attachment is to large"
        icon="warning-sign"
        description={`The attachment you are trying to open is too large. The 
          current file limit size is ~100kB, the file you are trying to open is 
          ${filesize}`}
      />
    )
  }

  return (
    <div style={{ maxHeight: '80vh', maxWidth: '80vh', overflow: 'auto' }}>
      {match(connection)
        .with({ status: 'loading' }, { status: 'idle' }, () => (
          <InlineSpinnerIcon />
        ))
        .with({ status: 'error' }, ({ error }) => <ErrorState error={error} />)
        .with({ status: 'success' }, ({ data }) =>
          match(data)
            .with({ dataType: 'json' }, ({ contents }) =>
              typeof contents === 'object' && contents !== null ? (
                <PrettyJson collapsed={false} src={contents} />
              ) : (
                // edge case (some primitive)
                <pre>{JSON.stringify(contents)}</pre>
              ),
            )
            .with({ dataType: 'text' }, ({ contents }) => <pre>{contents}</pre>)
            .exhaustive(),
        )
        .exhaustive()}
    </div>
  )
}
